<template>
    <div v-if="!!offer">
      <div v-if="offer.isLatest === false" class="alert alert-warning" role="alert">
        Ni zadnja ponudba za ta SDN, preveri povezani SDN.
      </div>
      <div class="row">
        <div class="col"><h1>{{ offer.numberDisplay }}</h1></div>
        <div class="col text-muted" v-show="offer.serviceOrderID && offer.serviceOrderID !== $EMPTY_GUID">
          <router-link :to="{ name: 'ServiceOrder', params: { workOrderId: offer.serviceOrderID } }">Povezan SDN</router-link>
        </div>
        <div class="col text-end">
          <h2><span class="badge bg-secondary position-relative">{{ offer.status }}</span></h2>
        </div>
      </div>

      <div v-if="offer.facility" class="h-100 p-4 bg-light border rounded-3 mb-3">
        <h5 class="mb-1">{{ offer.facility.name }}
          <small class="text-muted fw-normal ms-2">{{ offer.facility.facilityNumber }}</small>
        </h5>
        <div class="text-muted small">
          {{ offer.facility.address }}, {{ offer.facility.postCode }} {{ offer.facility.postCity }}
        </div>
        <div v-if="offer.facility.peculiarities" class="text-warning small mt-1">
          <i class="bi bi-exclamation-triangle me-1"></i>{{ offer.facility.peculiarities }}
        </div>
      </div>

      <div class="h-100 p-4 bg-light border rounded-3 mb-3">
        <WorkOrderMaterials
          ref="workOrderMaterialsEl"
          :canChange="offer.canChange"
          work-order-type="ServiceOrder"
          :workOrderId="offer.serviceOrderID"
          :showStock="$hasRoleStartsWith(role, ['Administrator', 'Lead'])"
          @changed="hasUnsavedChanges = true"
          :roundUp="true"
        />
      </div>


      <div class="h-100 p-4 bg-light border rounded-3 mb-3">
        <h4 class="col-2">Proste postavke</h4>
        <div
          v-if="getFieldError('lines')"
          class="alert alert-warning mb-3"
          role="alert"
        >
          <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
          {{ getFieldError('lines').message }}
        </div>
        <div class="table-responsive">
          <table class="table" role="table" v-show="offer.lines && offer.lines.length > 0">
            <thead>
              <tr>
                <th scope="col">Opis</th>
                <th scope="col">Količina</th>
                <th scope="col">Enota mere</th>
                <th scope="col">Cena na enoto</th>
                <th scope="col">Popust (%)</th>
                <th scope="col">Davčna stopnja</th>
                <th scope="col">Skupaj</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in offer.lines" :key="index">
                <td>
                  <input
                    type="text"
                    class="form-control"
                    v-model="item.description"
                    required
                    :data-field-path="'lines.' + index + '.description'"
                    data-field-label="Description"
                    data-error-message="Description is required"
                    :class="{ 'is-invalid': getFieldError('lines.' + index + '.description') }"
                  >
                  <div
                    v-if="getFieldError('lines.' + index + '.description')"
                    :id="'item-desc-error-' + index"
                    class="invalid-feedback"
                    role="alert"
                  >
                    {{ getFieldError('lines.' + index + '.description').message }}
                  </div>
                </td>
                <td>
                  <input
                    type="number"
                    class="form-control"
                    v-model="item.quantity"
                    min="0.01"
                    step="0.01"
                    required
                    :data-field-path="'lines.' + index + '.quantity'"
                    data-field-label="Quantity"
                    data-error-message="Quantity must be greater than 0"
                    @input="calculateItemTotal(index)"
                    :class="{ 'is-invalid': getFieldError('lines.' + index + '.quantity') }"
                  >
                  <div
                    v-if="getFieldError('lines.' + index + '.quantity')"
                    :id="'item-qty-error-' + index"
                    class="invalid-feedback"
                    role="alert"
                  >
                    {{ getFieldError('lines.' + index + '.quantity').message }}
                  </div>
                </td>
                <td>
                  <input
                    type="text"
                    class="form-control"
                    v-model="item.unit"
                  >
                </td>
                <td>
                  <input
                    type="number"
                    class="form-control"
                    v-model="item.unitPrice"
                    min="0.01"
                    step="0.01"
                    required
                    :data-field-path="'lines.' + index + '.unitPrice'"
                    data-field-label="Unit Price"
                    data-error-message="Unit price must be greater than 0"
                    @input="calculateItemTotal(index)"
                    :class="{ 'is-invalid': getFieldError('lines.' + index + '.unitPrice') }"
                  >
                  <div
                    v-if="getFieldError('lines.' + index + '.unitPrice')"
                    :id="'item-price-error-' + index"
                    class="invalid-feedback"
                    role="alert"
                  >
                    {{ getFieldError('lines.' + index + '.unitPrice').message }}
                  </div>
                </td>
                <td>
                  <input
                    type="number"
                    class="form-control"
                    v-model="item.discountRate"
                    step="0.01"
                    min="0"
                    max="100"
                    @input="calculateItemTotal(index)"
                    placeholder="0"
                  >
                </td>
                <td>
                  <select class="form-select" v-model="item.taxRate" @change="calculateTotals()">
                    <option :value="0">0%</option>
                    <option :value="9.5">9.5%</option>
                    <option :value="22">22%</option>
                  </select>
                </td>
                <td class="align-middle text-nowrap">{{ formatCurrency(item.total) }}</td>
                <td class="align-middle">
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    @click="removeItem(index)"
                  >
                    <span class="bi bi-trash fs-5 text-danger"> ✕ </span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <button type="button" class="btn btn-secondary" @click="addItem" :disabled="!offer.canChange">
            <i class="bi bi-plus-lg"></i> Add Item
          </button>
        </div>
        <div class="row justify-content-end mt-3">
          <div class="col-auto d-flex align-items-center gap-2">
            <label class="form-label mb-0 text-muted small">Opomnik (št.dni, 0 = ni opominjanja)</label>
            <input
              type="number"
              class="form-control form-control-sm"
              style="width: 8rem"
              v-model="offer.reminderDays"
              :disabled="!offer.canChange"
              step="1"
              min="0"
              max="14"
              placeholder="3"
              @input="calculateTotals()"
            >
          </div>
          <div class="col-auto d-flex align-items-center gap-2">
            <label class="form-label mb-0 text-muted small">Skupni popust (%)</label>
            <input
              type="number"
              class="form-control form-control-sm"
              style="width: 8rem"
              v-model="offer.discountRate"
              :disabled="!offer.canChange"
              step="0.1"
              min="0"
              max="100"
              placeholder="0"
              @input="calculateTotals()"
            >
          </div>
        </div>
        <div class="row justify-content-end mt-4 g-3">
          <div class="col-12 col-lg-4">
            <div class="border rounded bg-white h-100 p-3">
              <div class="text-muted small mb-2">Odločitve</div>
              <div v-if="!offer.signoffs || offer.signoffs.length === 0" class="text-muted small fst-italic">Ni poslano</div>
              <div v-for="s in offer.signoffs" :key="s.id" class="mb-2">
                <div class="d-flex align-items-center gap-2">
                  <span
                    class="badge mt-1 flex-shrink-0"
                    :class="{
                      'bg-warning text-dark': s.status === 'Pending',
                      'bg-success': s.status === 'Approved',
                      'bg-danger': s.status === 'Rejected',
                    }">
                    {{ s.status === 'Pending' ? 'Čaka' : s.status === 'Approved' ? 'Potrjeno' : 'Zavrnjeno' }}
                  </span>
                  <button
                    v-if="s.status === 'Pending'"
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    :disabled="isSaving"
                    @click="cancelSignoffAsync(s.id)"
                  >✕</button>
                </div>
                <div class="small text-truncate" :title="s.email">{{ s.email }}</div>
                <div class="text-muted">
                  <span v-if="s.status === 'Pending'">Poslano: {{ s.sent ? $dayjs(s.sent).format($DATE_FORMAT_DISPLAY) : '/' }}</span>
                  <span v-else>{{ s.status === 'Approved' ? 'Potrjeno' : 'Zavrnjeno' }}: {{ $dayjs(s.signedAt).format($DATE_FORMAT_DISPLAY) }}</span>
                </div>
              </div>
              <div class="text-muted small mt-2 border-top pt-2">Poslan opomnik</div>
              <div class="fw-semibold small">{{ offer.dateLastReminder ? $dayjs(offer.dateLastReminder).format($DATE_FORMAT_DISPLAY) : '/' }}</div>
            </div>
          </div>
          <div class="col-12 col-lg-5 col-xl-4">
            <div class="card">
              <div class="card-body p-0">
                <table class="table table-sm mb-0">
                  <tbody>
                    <tr>
                      <th class="ps-3 py-3 text-muted fw-normal">Skupaj proste postavke</th>
                      <td class="pe-3 py-3 text-end">{{ formatCurrency(lineSubtotal) }}</td>
                    </tr>
                    <tr v-if="globalDiscountAmount > 0">
                      <th class="ps-3 py-2 text-muted fw-normal">Skupni popust ({{ offer.discountRate }}%)</th>
                      <td class="pe-3 py-2 text-end text-danger">− {{ formatCurrency(globalDiscountAmount) }}</td>
                    </tr>
                    <tr>
                      <th class="ps-3 py-3 text-muted fw-normal">DDV za proste postavke</th>
                      <td class="pe-3 py-3 text-end">{{ formatCurrency(lineTax) }}</td>
                    </tr>
                    <tr>
                      <th class="ps-3 py-3 text-muted fw-normal">Skupaj material</th>
                      <td class="pe-3 py-3 text-end">{{ formatCurrency(offer.total || 0) }}</td>
                    </tr>
                    <tr class="table-light">
                      <th class="ps-3 py-3">Skupaj</th>
                      <td class="pe-3 py-3 text-end fw-bold">{{ formatCurrency(computedTotal) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mb-5 py-2 bg-light" style="position: sticky; bottom: 0; z-index: 1020; border-top: 4px solid #dee2e6;">
        <div class="col-10 offset-2 text-end">
          <span class="text-muted me-3">{{  offer.canChange ? "" : "SDN ni v statusu Ponudba ali to ni zadnja ponudba. Spremembe niso možne." }}</span>

          <button
            v-if="offer.status === 'Sent'"
            class="btn btn-outline-success btn-sm me-1 mb-2"
            type="button"
            :disabled="isSaving"
            @click="postCommandAsync({ Type: 'AuthorizedOfferDecisionCommand', OfferId: offer.id, Action: 'Approved', EncodedEmail: '' }, 'Ponudba potrjena.')"
          >
            ✅ Potrdi ponudbo
          </button>
          <button
            v-if="offer.status === 'Sent'"
            class="btn btn-outline-danger btn-sm me-3 mb-2"
            type="button"
            :disabled="isSaving"
            @click="postCommandAsync({ Type: 'AuthorizedOfferDecisionCommand', OfferId: offer.id, Action: 'Declined', EncodedEmail: '' }, 'Ponudba zavrnjena.')"
          >
            ❌ Zavrni ponudbo
          </button>

          <button
            class="btn btn-outline-secondary btn-sm me-3 mb-2"
            type="button"
            @click="$router.push({ name: 'Offers' })"
          >
            ✖️ Prekliči
          </button>
          <button
            class="btn btn-outline-primary btn-sm me-3 mb-2"
            type="button"
            :disabled="isSaving || !offer.canChange || !hasUnsavedChanges"
            @click="saveOfferAsync"
          >
            <span v-if="hasUnsavedChanges" class="blink-save-icon">💾</span>
            Shrani ponudbo
          </button>
          <button
                  class="btn btn-danger btn-sm me-3 mb-2"
                  type="button"
                  :disabled="isSaving || !offer.canChange"
                  @click="deleteOfferAsync"
          >
            🗑️ Izbriši ponudbo
          </button>
          <button
            class="btn btn-primary btn-sm me-3 mb-2"
            type="button"
            :disabled="isSaving || !offer.canChange || !offer.canSendOffer || hasUnsavedChanges"
            @click="sendOfferAsync"
          >
            📨 Pošlji ponudbo
          </button>
        </div>
      </div>

      <div class="row mt-4" v-show="attachmentUrl && !hasUnsavedChanges">
        <div class="col-12" style="min-height: 600px; height: calc(100vh - 70px);">
          <embed :src="attachmentUrl" type="application/pdf" width="100%" height="100%">
        </div>
      </div>

    </div>
</template>

<script setup>
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject, nextTick } from "vue"
import { useAuth, role } from "../composition-api/useAuth"
import { clearNotifications, setError, setSuccess, startLoading, stopLoading } from "../components/Notification"
import { getEntityAsync } from "scripts/api"
import { DATETIME_FORMAT, EMPTY_GUID, DATE_FORMAT } from "../utils/constants"

defineOptions({ name: "OfferEdit" })

const { fetchAuthAsync } = useAuth()
const route = VueRouter.useRoute()
const offer = ref(null)
const isSaving = ref(false)
const hasUnsavedChanges = ref(false)
const workOrderMaterialsEl = ref(null)

const getLineNetTotal = (line) => {
  const quantity = parseFloat(line?.quantity) || 0
  const unitPrice = parseFloat(line?.unitPrice) || 0
  const discountRate = parseFloat(line?.discountRate) || 0
  const discount = (quantity * unitPrice * discountRate) / 100
  return quantity * unitPrice - discount
}

const normalizeOffer = (loadedOffer) => {
  if (!loadedOffer) {
    return loadedOffer
  }

  loadedOffer.lines = (loadedOffer.lines || []).map((line) => ({
    ...line,
    total: getLineNetTotal(line),
  }))

  return loadedOffer
}

watch(
  offer,
  () => {
    hasUnsavedChanges.value = true
  },
  { deep: true },
)

watch(
  () => route.params.offerId,
  async () => {
    console.log("Fetching offer with id: ", route.params.offerId)
    offer.value = normalizeOffer(await getEntityAsync(fetchAuthAsync, "Offer", route.params.offerId))
    await nextTick()
    hasUnsavedChanges.value = false
  },
)

const attachmentUrl = computed(() =>
  offer.value && offer.value.attachmentID && offer.value.attachmentID !== EMPTY_GUID
    ? `${AZURE_URI}/Attachment/${offer.value.attachmentID}`
    : "",
)

onMounted(async () => {
  console.log("Mounted OfferEdit component with offerId: ", route.params.offerId)
  offer.value = normalizeOffer(await getEntityAsync(fetchAuthAsync, "Offer", route.params.offerId))
  await nextTick()
  hasUnsavedChanges.value = false
})

const lineSubtotal = computed(() =>
  (offer.value?.lines || []).reduce((sum, item) => sum + getLineNetTotal(item), 0),
)

const lineTax = computed(() =>
  (offer.value?.lines || []).reduce(
    (sum, item) => sum + getLineNetTotal(item) * ((parseFloat(item.taxRate) || 0) / 100),
    0,
  ),
)

const globalDiscountAmount = computed(
  () => lineSubtotal.value * ((parseFloat(offer.value?.discountRate) || 0) / 100),
)

const computedTotal = computed(
  () => (parseFloat(offer.value?.total) || 0) + lineSubtotal.value - globalDiscountAmount.value + lineTax.value,
)

const formatCurrency = (value) => `${(parseFloat(value) || 0).toFixed(2)} €`

const buildOfferLinesPayload = () => ({
  offerId: offer.value?.id,
  discountRate: parseFloat(offer.value?.discountRate) || 0,
  reminderDays: offer.value?.reminderDays != null ? parseInt(offer.value.reminderDays) : 3,
  lines: (offer.value?.lines || []).map((line, index) => ({
    id: line.id,
    lineNumber: line.lineNumber ?? index + 1,
    description: line.description,
    quantity: parseFloat(line.quantity) || 0,
    unit: line.unit,
    unitPrice: parseFloat(line.unitPrice) || 0,
    discountRate: parseFloat(line.discountRate) || 0,
    taxRate: parseFloat(line.taxRate) || 0,
  })),
})

const saveOfferAsync = async () => {
  if (!offer.value?.id) {
    return
  }

  isSaving.value = true
  startLoading("saveOfferAsync")
  try {
    clearNotifications()
    const raw = await fetchAuthAsync(
      `${AZURE_URI}/Offer/${offer.value.id}/Lines`,
      "PATCH",
      buildOfferLinesPayload(),
    )
    if (raw?.status !== "ok") {
      throw raw
    }
    setSuccess("Ponudba shranjena.")
    offer.value = normalizeOffer(await getEntityAsync(fetchAuthAsync, "Offer", offer.value.id))
    await nextTick()
    hasUnsavedChanges.value = false
    workOrderMaterialsEl.value?.reload()
  } catch (e) {
    setError(e)
  }
  stopLoading("saveOfferAsync")
  isSaving.value = false
}

const postCommandAsync = async (command, successMessage, { reloadMaterials = false } = {}) => {
  if (!offer.value?.id) return
  isSaving.value = true
  startLoading("postCommandAsync")
  try {
    clearNotifications()
    const raw = await fetchAuthAsync(`${AZURE_URI}/Offer/${offer.value.id}/Command`, "POST", command)
    if (raw?.status !== "ok") {
      throw raw
    }
    setSuccess(successMessage)
    offer.value = normalizeOffer(await getEntityAsync(fetchAuthAsync, "Offer", offer.value.id))
    await nextTick()
    hasUnsavedChanges.value = false
    if (reloadMaterials) workOrderMaterialsEl.value?.reload()
  } catch (e) {
    setError(e)
  }
  stopLoading("postCommandAsync")
  isSaving.value = false
}

const deleteOfferAsync = () =>
  postCommandAsync({ Type: "OfferDeleteCommand", OfferId: offer.value.id }, "Zbrisano.")

const sendOfferAsync = async () => {
  if (!offer.value?.id || !offer.value?.serviceOrderID || offer.value?.serviceOrderID === EMPTY_GUID) {
    return
  }

  isSaving.value = true
  startLoading("sendOfferAsync")
  try {
    clearNotifications()
    const raw = await fetchAuthAsync(`${AZURE_URI}/Offer/${offer.value.serviceOrderID}`, "PATCH", {
      offerId: offer.value.id,
      isPurchaseOrderRequest: !!offer.value.isPurchaseOrderRequest,
    })
    if (raw?.status !== "ok") {
      throw raw
    }
    setSuccess("Ponudba poslana.")
    offer.value = normalizeOffer(await getEntityAsync(fetchAuthAsync, "Offer", offer.value.id))
    await nextTick()
    hasUnsavedChanges.value = false
    workOrderMaterialsEl.value?.reload()
  } catch (e) {
    setError(e)
  }
  stopLoading("sendOfferAsync")
  isSaving.value = false
}

const cancelSignoffAsync = (signoffId) => {
  if (!signoffId) return
  postCommandAsync({ Type: "CancelSignoffCommand", OfferId: offer.value.id, SignoffId: signoffId }, "Preklicano.")
}

const validationErrors = ref([])
const fieldErrors = ref({})

const getFieldError = (fieldPath) => fieldErrors.value[fieldPath]

const clearAllErrors = () => {
  validationErrors.value = []
  fieldErrors.value = {}
}

const addItem = () => {
  offer.value.lines.push({
    description: "",
    quantity: 1,
    unitPrice: 750,
    total: 0,
    taxRate: 22,
  })
  calculateItemTotal(offer.value.lines.length - 1)
  clearAllErrors()
}

const removeItem = (index) => {
  offer.value.lines.splice(index, 1)
  calculateTotals()
}

const calculateItemTotal = (index) => {
  const item = offer.value.lines[index]
  item.total = getLineNetTotal(item)
  calculateTotals()
}

const calculateTotals = () => {
  offer.value.subtotal = lineSubtotal.value
  offer.value.tax = lineTax.value
  clearAllErrors()
}
</script>
